import React, { useState, useMemo, useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import {
  Button,
  UnawardIcon,
  FilterIcon,
  OverlayService,
  Flyout,
  Theme,
  CheckboxWithLabel,
  Select,
  SelectOption,
  Timing,
  SparkleIcon,
  AlertService,
} from '@spoiler-alert/ui-library';
import capitalize from 'capitalize';
import TrucklaneBlock from './trucklane-block';
import { OfferComparisonStrings } from '../../string-resources';
import { UserFilters } from '../../store';
import {
  buildFilterOptions,
  includeCustomerFilter,
  includeLocationFilter,
  includeLogisticsFilter,
  includeOfferStatusFilter,
  includeTruckLaneStatusFilter,
  includeTruckTypeFilter,
  searchData,
} from './trucklane-filter-utility';
import subscriptionTier from '../../enums/subscription-tier';
import OfferShiftContext from '../offer-comparison/offer-shift-context';
import { offerlistingStatuses, featureFlags } from '../../enums';
import { checkFeature } from '../../helpers';
import { Stepper } from '../../components/stepper/stepper';
import { WorkflowEnforcement } from '../../components/banners/workflow-enforcement';
import StaticLoading from '../static-loading';
import { ACTIVE_STEP } from '../offer-comparison/active-step-enums';
import TrucklaneSearch from './trucklane-search';

const styles = {
  spacing: {
    paddingBottom: 36,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  marginRow: {
    extend: 'row',
    marginBottom: 24,
  },
  stickyRow: {
    extend: 'row',
    position: 'sticky',
    zIndex: 9,
    top: '45px',
    padding: '24px 0px',
    backgroundColor: Theme.white,
  },
  stickyRowPadding: {
    padding: '12px',
  },
  filterFlyout: {
    extend: 'marginRow',
    '& h4': {
      margin: 0,
      marginRight: 'auto',
      fontSize: '1rem',
      fontWeight: 500,
    },
    '& a': {
      color: Theme.linkTextColor,
      cursor: 'pointer',
      fontSize: '0.875rem',
    },
  },
  filterHeader: {
    marginBottom: 24,
    '& h2': {
      fontSize: '14px',
      fontWeight: 500,
      marginBottom: 0,
    },
  },
  navView: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 24,
  },
  suggestionStepContent: {
    flex: 1,
    padding: '24px 0 24px 24px',
    borderRadius: 6,
    border: '1px solid transparent',
    background: `radial-gradient(ellipse farthest-side at top left, #F5FBF6 0%, #FFF 70%) padding-box, radial-gradient(ellipse farthest-side at top left, ${Theme.primaryColor} 0%, #FFF 70%) border-box`,
  },
  exceptionStepContent: {
    flex: 1,
    padding: '24px 0 24px 24px',
    borderRadius: 6,
    border: '1px solid transparent',
    background:
      'radial-gradient(ellipse farthest-side at top left, #EEF7F9 0%, #FFF 70%) padding-box, radial-gradient(ellipse farthest-side at top left, #71BBCD 0%, #FFF 70%) border-box',
  },
  nonSuggestedStepContent: {
    flex: 1,
    padding: '24px 0 24px 24px',
    borderRadius: 6,
    border: '1px solid transparent',
    background: `radial-gradient(ellipse farthest-side at top left, #F3F3F4 0%, #FFF 70%) padding-box, radial-gradient(ellipse farthest-side at top left, ${Theme.borderColor} 0%, #FFF 70%) border-box`,
  },
  loadingContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: 24,
  },
};

const useStyles = createUseStyles(styles);
const defaultFilterState = {
  locations: [],
  customers: [],
  logistics: [],
  trucklaneStatus: [],
  truckType: [],
  offerStatus: [],
};

const TrucklaneFilter = ({
  user,
  history,
  originalTrucklanes,
  rpeTrucklanes,
  rpeLoading,
  openDrilldown,
  activeStep,
  setActiveStep,
  metrics,
  recalculation,
}) => {
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);
  const redirectBuyerSiteName = history.location.state?.buyerSiteName;
  const redirectBuyerSiteId = history.location.state?.buyerSiteId;
  const redirectOfferStatus = history.location.state?.offerStatus;
  const redirectFilterState = {
    ...defaultFilterState,
    ...(redirectBuyerSiteName && { customers: [{ text: redirectBuyerSiteName, value: redirectBuyerSiteId }] }),
    ...(redirectOfferStatus && { offerStatus: [redirectOfferStatus] }),
  };
  const classes = useStyles();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(redirectFilterState ? { ...redirectFilterState } : { ...defaultFilterState });
  const [searchText, setSearchText] = useState('');
  const saasLiteCustomer = user.site.subscriptionTier === subscriptionTier.saasLite;
  const { offerShiftsMappedToTrucklanes = new Map() } = useContext(OfferShiftContext) || {};
  const filterOptions = useMemo(() => buildFilterOptions(originalTrucklanes), [originalTrucklanes]);

  const { suggestedTrucklanes, nonSuggestedTrucklanes, activeNonSuggestedTrucklanes, inactiveNonSuggestedTrucklanes } = useMemo(() => {
    const filteredTrucklanes = new Map();
    originalTrucklanes.forEach((trucklane) => {
      trucklane.offers.forEach((offer) => {
        if (filteredTrucklanes.has(trucklane.id)) return;
        if (
          searchData(offer, searchText) &&
          includeLocationFilter(offer, filters) &&
          includeCustomerFilter(offer, filters) &&
          includeLogisticsFilter(offer, filters) &&
          includeTruckTypeFilter(offer, filters) &&
          includeOfferStatusFilter(offer, filters) &&
          includeTruckLaneStatusFilter(trucklane.offers, filters)
        ) {
          filteredTrucklanes.set(trucklane.id, trucklane);
        }
      });
    });
    const lanes = [...filteredTrucklanes.values()];
    const activeNonSuggestedTrucklanes = [];
    const inactiveNonSuggestedTrucklanes = [];
    lanes.forEach((ft) => {
      if (!ft.hasSuggestions && !(offerShiftsMappedToTrucklanes && offerShiftsMappedToTrucklanes.has(ft.id))) {
        const hasAwardedOrAccepted = ft.offers.some((offer) => [offerlistingStatuses.ACCEPTED, offerlistingStatuses.AWARDED].includes(offer.status));
        if (hasAwardedOrAccepted) {
          activeNonSuggestedTrucklanes.push(ft);
        } else if (!hasAwardedOrAccepted) {
          inactiveNonSuggestedTrucklanes.push(ft);
        }
      }
    });
    return {
      suggestedTrucklanes: lanes.filter(
        (lane) => lane.hasSuggestions || (offerShiftsMappedToTrucklanes && offerShiftsMappedToTrucklanes.has(lane.id)),
      ),
      nonSuggestedTrucklanes: [...activeNonSuggestedTrucklanes, ...inactiveNonSuggestedTrucklanes],
      activeNonSuggestedTrucklanes,
      inactiveNonSuggestedTrucklanes,
    };
  }, [originalTrucklanes, searchText, filters, offerShiftsMappedToTrucklanes]);

  const unfilteredSuggestedTrucklanesCount = useMemo(
    () =>
      originalTrucklanes.filter((lane) => lane.hasSuggestions || (offerShiftsMappedToTrucklanes && offerShiftsMappedToTrucklanes.has(lane.id)))
        .length,
    [originalTrucklanes, offerShiftsMappedToTrucklanes],
  );

  const trucklanesWithReservePriceExceptions = useMemo(() => {
    const filteredTrucklanes = new Map();
    rpeTrucklanes?.forEach((trucklane) => {
      trucklane.offers.forEach((offer) => {
        if (filteredTrucklanes.has(trucklane.id)) return;
        if (
          searchData(offer, searchText) &&
          includeLocationFilter(offer, filters) &&
          includeCustomerFilter(offer, filters) &&
          includeLogisticsFilter(offer, filters) &&
          includeTruckTypeFilter(offer, filters) &&
          includeOfferStatusFilter(offer, filters) &&
          includeTruckLaneStatusFilter(trucklane.offers, filters)
        ) {
          filteredTrucklanes.set(trucklane.id, trucklane);
        }
      });
    });
    const lanes = [...filteredTrucklanes.values()];

    return lanes.filter((lane) => lane.offers.some((offer) => offer.suggestions?.reservePriceException?.suggested));
  }, [rpeTrucklanes, searchText, filters]);

  const clearFilters = () => {
    setFilters({
      locations: [],
      customers: [],
      logistics: [],
      trucklaneStatus: [],
      truckType: [],
      offerStatus: [],
    });
    UserFilters.updateFilters('trucklane', {
      locations: [],
      customers: [],
      logistics: [],
      trucklaneStatus: [],
      truckType: [],
      offerStatus: [],
    });
    document.getElementById('text-search').children[2].click();
  };

  const filtersAppliedCount = useMemo(() => {
    return Object.keys(filters).filter((fkey) => {
      const filter = filters[fkey];
      if (Array.isArray(filter)) {
        return filter.length > 0;
      }
      if (filter === true) return true;
      if (filter.min !== undefined || filter.max !== undefined) return true;
      return false;
    }).length;
  }, [filters]);

  const handleFilterChange = useCallback(
    (prop, value) => {
      setFilters({
        ...filters,
        [prop]: value,
      });
    },
    [filters],
  );

  const removeItem = (array, item) => {
    const index = array.indexOf(item);
    if (index > -1) {
      array.splice(index, 1);
    }
    return array;
  };

  const handleCheckFilterChange = useCallback((prop, event) =>
    handleFilterChange(prop, event.checked ? [...filters[prop], event.value] : removeItem(filters[prop], event.value), [handleFilterChange]),
  );

  const renderedFilters = useMemo(() => {
    return [
      <Select
        key="locations"
        label="Location"
        multiple
        search
        onChange={handleFilterChange.bind(this, 'locations')}
        selectedItems={filters.locations}
      >
        {filterOptions.locations.map((location) => (
          <SelectOption checkbox key={location.id} value={location.id}>
            {location.name}
          </SelectOption>
        ))}
      </Select>,
      <Select
        key="customers"
        label="Customers"
        multiple
        search
        onChange={handleFilterChange.bind(this, 'customers')}
        selectedItems={filters.customers}
      >
        {filterOptions.customers.map((customer) => (
          <SelectOption checkbox key={customer.id} value={customer.id}>
            {customer.name}
          </SelectOption>
        ))}
      </Select>,
      filterOptions.logistics.length > 1 && (
        <div key="logistics" className={classes.filterHeader}>
          <h2>Logistics</h2>
          {filterOptions.logistics.map((logistic) => (
            <CheckboxWithLabel
              id={logistic}
              label={logistic}
              key={logistic}
              value={logistic}
              checked={filters.logistics.includes(logistic)}
              onChecked={handleCheckFilterChange.bind(this, 'logistics')}
            />
          ))}
        </div>
      ),
      filterOptions.trucklaneStatus.length > 1 && (
        <div key="laneStatus" className={classes.filterHeader}>
          <h2>Truck Lane Status</h2>
          {filterOptions.trucklaneStatus.map((status) => (
            <CheckboxWithLabel
              id={`lane-${status}`}
              label={status}
              key={`lane-${status}`}
              value={status}
              checked={filters.trucklaneStatus.includes(status)}
              onChecked={handleCheckFilterChange.bind(this, 'trucklaneStatus')}
            />
          ))}
        </div>
      ),
      filterOptions.truckType.length > 1 && (
        <div key="truckType" className={classes.filterHeader}>
          <h2>Truck Type</h2>
          {filterOptions.truckType.map((truckType) => (
            <CheckboxWithLabel
              id={`truckType-${truckType}`}
              label={truckType}
              key={`truckType-${truckType}`}
              value={truckType}
              checked={filters.truckType.includes(truckType)}
              onChecked={handleCheckFilterChange.bind(this, 'truckType')}
            />
          ))}
        </div>
      ),
      filterOptions.offerStatus.length > 1 && (
        <div key="offerStatus" className={classes.filterHeader}>
          <h2>Offer Status</h2>
          {filterOptions.offerStatus.map((status) => (
            <CheckboxWithLabel
              id={`offer-${status}`}
              label={capitalize(status)}
              key={`offer-${status}`}
              value={status}
              checked={filters.offerStatus.includes(status)}
              onChecked={handleCheckFilterChange.bind(this, 'offerStatus')}
            />
          ))}
        </div>
      ),
    ];
  }, [filters, filterOptions]);

  const toggleFilters = () => {
    if (showFilters) {
      OverlayService.hide();
      UserFilters.updateFilters('trucklane', filters);
    } else {
      OverlayService.show();
    }
    setShowFilters(!showFilters);
  };

  const handleSearch = useMemo(
    () =>
      Timing.debounce((text) => {
        setSearchText(text);
      }, 300),
    [],
  );

  const displaySettings = () => {
    history.push('/organization/settings/smart-awarding');
    return;
  };

  const getOpportunities = useMemo(() => {
    if (rpeLoading || rpeTrucklanes === undefined) {
      return (
        <div className={classes.loadingContainer}>
          <StaticLoading />
        </div>
      );
    }
    return (
      <div className={classes.exceptionStepContent}>
        {suggestedTrucklanes.length > 0 && <WorkflowEnforcement setActiveStep={setActiveStep} />}
        <TrucklaneBlock
          title="Award Opportunities"
          trucklaneData={trucklanesWithReservePriceExceptions || []}
          buttonDetails={{ title: 'Award Opportunities', icon: SparkleIcon, loadingText: 'Awarding Opportunities...' }}
          history={history}
          user={user}
          filters={filtersAppliedCount > 0 || !!searchText}
          clearFilters={clearFilters}
          openDrilldown={openDrilldown}
          metrics={metrics}
          handleSearch={handleSearch}
          toggleFilters={toggleFilters}
          filtersAppliedCount={filtersAppliedCount}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          searchText={searchText}
          recalculation={recalculation}
        />
      </div>
    );
  }, [
    rpeTrucklanes,
    rpeLoading,
    suggestedTrucklanes,
    trucklanesWithReservePriceExceptions,
    filtersAppliedCount,
    searchText,
    history,
    user,
    openDrilldown,
    metrics,
    activeStep,
    setActiveStep,
    clearFilters,
    handleSearch,
    toggleFilters,
    recalculation,
  ]);

  const getTrucklaneBlockWithStepper = () => {
    return (
      <div className={classes.navView}>
        <Stepper activeStep={activeStep} setActiveStep={setActiveStep} />
        {activeStep === ACTIVE_STEP.SUGGESTIONS && (
          <div className={classes.suggestionStepContent}>
            <TrucklaneBlock
              title="Award Suggestions"
              trucklaneData={suggestedTrucklanes}
              unfilteredSuggestedTrucklanesCount={unfilteredSuggestedTrucklanesCount}
              buttonDetails={{ title: 'Award Suggestions', icon: SparkleIcon, loadingText: 'Awarding Suggestions...' }}
              history={history}
              user={user}
              filters={filtersAppliedCount > 0 || !!searchText}
              clearFilters={clearFilters}
              openDrilldown={openDrilldown}
              handleSearch={handleSearch}
              toggleFilters={toggleFilters}
              filtersAppliedCount={filtersAppliedCount}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              searchText={searchText}
              recalculation={recalculation}
            />
          </div>
        )}
        {activeStep === ACTIVE_STEP.OPPORTUNITIES && getOpportunities}
        {activeStep === ACTIVE_STEP.ACTIVE_LANES && (
          <div className={classes.nonSuggestedStepContent}>
            <TrucklaneBlock
              title="Active Lanes"
              trucklaneData={activeNonSuggestedTrucklanes}
              buttonDetails={{ title: 'Unaward All', icon: UnawardIcon, warning: true, loadingText: 'Unawarding...' }}
              history={history}
              user={user}
              filters={filtersAppliedCount > 0 || !!searchText}
              clearFilters={clearFilters}
              openDrilldown={openDrilldown}
              handleSearch={handleSearch}
              toggleFilters={toggleFilters}
              filtersAppliedCount={filtersAppliedCount}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              searchText={searchText}
              recalculation={recalculation}
            />
          </div>
        )}
        {activeStep === ACTIVE_STEP.INACTIVE_LANES && (
          <div className={classes.nonSuggestedStepContent}>
            <TrucklaneBlock
              title="Inactive Lanes"
              trucklaneData={inactiveNonSuggestedTrucklanes}
              buttonDetails={{ title: 'Unaward All', icon: UnawardIcon, warning: true, loadingText: 'Unawarding...' }}
              history={history}
              user={user}
              filters={filtersAppliedCount > 0 || !!searchText}
              clearFilters={clearFilters}
              openDrilldown={openDrilldown}
              handleSearch={handleSearch}
              toggleFilters={toggleFilters}
              filtersAppliedCount={filtersAppliedCount}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              searchText={searchText}
              recalculation={recalculation}
            />
          </div>
        )}
      </div>
    );
  };

  const getTrucklaneBlockWithoutStepper = () => {
    return (
      <>
        <div className={classes.spacing}>
          <TrucklaneBlock
            title="Smart Award Suggestions"
            trucklaneData={suggestedTrucklanes}
            buttonDetails={{ title: 'Award Suggestions', icon: SparkleIcon, loadingText: 'Awarding Suggestions...' }}
            history={history}
            user={user}
            activeStep={ACTIVE_STEP.SUGGESTIONS}
            filters={filtersAppliedCount > 0 || !!searchText}
            clearFilters={clearFilters}
            openDrilldown={openDrilldown}
            recalculation={recalculation}
          />
        </div>
        <TrucklaneBlock
          title="No Suggestions"
          trucklaneData={nonSuggestedTrucklanes}
          buttonDetails={{ title: 'Unaward All', icon: UnawardIcon, warning: true, loadingText: 'Unawarding...' }}
          history={history}
          user={user}
          filters={filtersAppliedCount > 0 || !!searchText}
          clearFilters={clearFilters}
          openDrilldown={openDrilldown}
          activeStep={ACTIVE_STEP.ACTIVE_LANES}
          recalculation={recalculation}
        />
      </>
    );
  };

  return (
    <div>
      <div className={`${classes.stickyRow} ${reservePriceExceptionsEnabled ? classes.stickyRowPadding : ''}`}>
        {reservePriceExceptionsEnabled ? null : (
          <TrucklaneSearch
            handleSearch={handleSearch}
            toggleFilters={toggleFilters}
            filtersAppliedCount={filtersAppliedCount}
            fullScreen
            searchText={searchText}
          />
        )}
        {!saasLiteCustomer && !reservePriceExceptionsEnabled && (
          <Button secondary onClick={displaySettings}>
            Smart Award Settings
          </Button>
        )}
      </div>
      {reservePriceExceptionsEnabled ? getTrucklaneBlockWithStepper() : getTrucklaneBlockWithoutStepper()}
      <Flyout position="right" open={showFilters} onHide={toggleFilters}>
        <div className={classes.filterFlyout} data-element="filters-header">
          <h4>{OfferComparisonStrings.filters}</h4>
          <a tabIndex="1" onClick={clearFilters} data-element="clear-filters">
            {OfferComparisonStrings.clearFilters}
          </a>
        </div>
        {renderedFilters}
      </Flyout>
    </div>
  );
};

TrucklaneFilter.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  originalTrucklanes: PropTypes.array,
  rpeTrucklanes: PropTypes.array,
  rpeLoading: PropTypes.bool,
  openDrilldown: PropTypes.func,
  metrics: PropTypes.array,
  recalculation: PropTypes.bool,
};

export default TrucklaneFilter;
